import { PaginationParams } from '@/lib/types';
import { api, ApiResponse } from './api';

export const patientService = {
  savePatient: (patientData: object): Promise<ApiResponse<object>> => {
    return api.post('clinic/save-patient', patientData);
  },

  /**
   * Stub-create a patient from the guided enrollment "New patient" form.
   * Only an email contact is required; the backend fills in placeholder
   * values for first/last name and DOB which the patient overwrites at
   * intake. Returns the created patient (or an existing match by email).
   */
  createGuidedPatient: (
    clinicId: string,
    patientData: object,
  ): Promise<ApiResponse<{ id: string; full_name?: string; first_name?: string; last_name?: string; email?: string; phone?: string; dob?: string; gender?: string; status?: string }>> => {
    return api.post(`/clinic/${clinicId}/guided-patient`, patientData);
  },

  updatePatient: (id: string, patientData: object): Promise<ApiResponse<object>> => {
    return api.put(`/clinic/patients/${id}`, patientData);
  },

  updatePatientStatus: (id: string, status: 'active' | 'inactive'): Promise<ApiResponse<object>> => {
    return api.put(`/clinic/patients/${id}/status`, { status });
  },

  getPatient: (params?: PaginationParams) => {
    const payload = {
      page: params?.page,
      limit: params?.limit,
      search: params?.search,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      practitioners: params?.filter?.practitioners || [],
      clinics: params?.filter?.clinics || [],
      genders: params?.filter?.genders || [],
      statuses: params?.filter?.statuses || [],
      dobFrom: params?.filter?.dobFrom,
      dobTo: params?.filter?.dobTo,
    };
    return api.post<any>(`/clinic/patients/list`, payload);
  },

  getPatientById: (id: string) => {
    return api.get<any>(`/clinic/patients/${id}`);
  },

  getPatientsForDropdown: (search: string, clinic_id: string) => {
    return api.post('/clinic/patients/get-patient-dropdown', {search, clinic_id})
  },

  getPatientAuditLogs: (patientId: string) => {
    return api.get<any>(`/clinic/patients/${patientId}/audit-logs`);
  },

  getPatientTimeline: (
    patientId: string,
    params?: {
      limit?: number;
      offset?: number;
      eventTypes?: string[];
      startDate?: string;
      endDate?: string;
    }
  ) => {
    const queryParams = new URLSearchParams();
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());
    if (params?.eventTypes?.length) queryParams.append('eventTypes', params.eventTypes.join(','));
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const suffix = queryParams.toString();
    return api.get<any>(`/clinic/patients/${patientId}/timeline${suffix ? `?${suffix}` : ''}`);
  },

  // Patient form link methods
  generatePatientFormLink: (data: {
    email: string;
    patient_id?: string;
    clinic_id: string;
    practitioner_id: string;
  }): Promise<ApiResponse<{ link: string; expires_at: string }>> => {
    return api.post(`/clinic/${data.clinic_id}/patients/generate-form-link`, data);
  },

  validatePatientFormToken: (token: string): Promise<ApiResponse<any>> => {
    return api.get(`/public/patient-form/${token}`);
  },

  submitPatientForm: (token: string, data: object): Promise<ApiResponse<{ patient_id: string }>> => {
    return api.post(`/public/patient-form/${token}`, data);
  },

  getPatientFormLinks: (clinicId: string, params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  }): Promise<ApiResponse<any>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    
    return api.get(`/clinic/${clinicId}/patients/form-links?${queryParams.toString()}`);
  },

  revokePatientFormLink: (clinicId: string, linkId: string): Promise<ApiResponse<any>> => {
    return api.put(`/clinic/${clinicId}/patients/form-links/${linkId}/revoke`);
  },

  resendPatientFormLink: (clinicId: string, linkId: string): Promise<ApiResponse<any>> => {
    return api.post(`/clinic/${clinicId}/patients/form-links/${linkId}/resend`);
  },

  getHealthMetricHistory: (
    patientId: string,
    params?: { metric_type?: string; limit?: number; offset?: number }
  ) => {
    const queryParams = new URLSearchParams();
    if (params?.metric_type) queryParams.append('metric_type', params.metric_type);
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());

    const suffix = queryParams.toString();
    return api.get<any>(`/clinic/patients/${patientId}/health-metric-history${suffix ? `?${suffix}` : ''}`);
  },
};
